/**
 * DOM utility functions to replace jQuery
 * Provides jQuery-like API for modern vanilla JavaScript
 */

export type DOMElement = HTMLElement | null;
export type DOMElementArray = HTMLElement[];

/**
 * jQuery-like $() selector function
 * Returns single element or array of elements
 */
export function $(selector: string | HTMLElement): DOMElement | DOMElementArray {
    if (typeof selector === 'string') {
        const elements = document.querySelectorAll<HTMLElement>(selector);
        return elements.length === 1 ? elements[0] : Array.from(elements);
    }
    return selector;
}

/**
 * Query single element
 */
export function query<T extends HTMLElement = HTMLElement>(selector: string): T | null {
    return document.querySelector<T>(selector);
}

/**
 * Query all elements
 */
export function queryAll<T extends HTMLElement = HTMLElement>(selector: string): T[] {
    return Array.from(document.querySelectorAll<T>(selector));
}

/**
 * Create element with optional attributes and children
 */
export function createElement<T extends HTMLElement = HTMLElement>(
    tag: string,
    attributes?: Record<string, string | boolean>,
    ...children: (Node | string)[]
): T {
    const element = document.createElement(tag) as T;
    
    if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
            if (key === 'class') {
                element.className = String(value);
            } else if (key === 'text') {
                element.textContent = String(value);
            } else if (typeof value === 'boolean') {
                if (value) {
                    element.setAttribute(key, '');
                }
            } else {
                element.setAttribute(key, String(value));
            }
        });
    }
    
    children.forEach(child => {
        if (typeof child === 'string') {
            element.appendChild(document.createTextNode(child));
        } else {
            element.appendChild(child);
        }
    });
    
    return element;
}

/**
 * Helper to work with elements (single or array)
 */
export class DOMHelper {
    private elements: HTMLElement[];

    constructor(selector: string | HTMLElement | HTMLElement[]) {
        if (typeof selector === 'string') {
            this.elements = Array.from(document.querySelectorAll<HTMLElement>(selector));
        } else if (Array.isArray(selector)) {
            this.elements = selector;
        } else if (selector) {
            this.elements = [selector];
        } else {
            this.elements = [];
        }
    }

    get length(): number {
        return this.elements.length;
    }

    get(index: number): HTMLElement | undefined {
        return this.elements[index];
    }

    [Symbol.iterator]() {
        return this.elements[Symbol.iterator]();
    }

    // jQuery-like methods
    text(content?: string): this | string {
        if (content === undefined) {
            return this.elements[0]?.textContent || '';
        }
        this.elements.forEach(el => el.textContent = content);
        return this;
    }

    html(content?: string): this | string {
        if (content === undefined) {
            return this.elements[0]?.innerHTML || '';
        }
        this.elements.forEach(el => el.innerHTML = content);
        return this;
    }

    append(...nodes: (Node | string)[]): this {
        this.elements.forEach(el => {
            nodes.forEach(node => {
                if (typeof node === 'string') {
                    // Check if string contains HTML tags - if so, use innerHTML
                    // Otherwise use text node for safety
                    if (node.includes('<') && node.includes('>')) {
                        // Use insertAdjacentHTML to append HTML safely
                        el.insertAdjacentHTML('beforeend', node);
                    } else {
                        el.appendChild(document.createTextNode(node));
                    }
                } else {
                    el.appendChild(node);
                }
            });
        });
        return this;
    }

    prepend(...nodes: (Node | string)[]): this {
        this.elements.forEach(el => {
            nodes.reverse().forEach(node => {
                if (typeof node === 'string') {
                    el.insertBefore(document.createTextNode(node), el.firstChild);
                } else {
                    el.insertBefore(node, el.firstChild);
                }
            });
        });
        return this;
    }

    empty(): this {
        this.elements.forEach(el => el.innerHTML = '');
        return this;
    }

    remove(): this {
        this.elements.forEach(el => el.remove());
        return this;
    }

    addClass(...classes: string[]): this {
        this.elements.forEach(el => el.classList.add(...classes));
        return this;
    }

    removeClass(...classes: string[]): this {
        this.elements.forEach(el => el.classList.remove(...classes));
        return this;
    }

    toggleClass(className: string, force?: boolean): this {
        this.elements.forEach(el => el.classList.toggle(className, force));
        return this;
    }

    hasClass(className: string): boolean {
        return this.elements[0]?.classList.contains(className) || false;
    }

    attr(name: string, value?: string): this | string | null {
        if (value === undefined) {
            return this.elements[0]?.getAttribute(name) || null;
        }
        this.elements.forEach(el => el.setAttribute(name, value));
        return this;
    }

    removeAttr(name: string): this {
        this.elements.forEach(el => el.removeAttribute(name));
        return this;
    }

    prop(name: string, value?: any): this | any {
        if (value === undefined) {
            return (this.elements[0] as any)?.[name];
        }
        this.elements.forEach(el => (el as any)[name] = value);
        return this;
    }

    css(property: string, value: string): this;
    css(property: Record<string, string>): this;
    css(property: string): string;
    css(property: string | Record<string, string>, value?: string): this | string {
        if (typeof property === 'object') {
            this.elements.forEach(el => {
                Object.entries(property).forEach(([key, val]) => {
                    (el.style as any)[key] = val;
                });
            });
            return this;
        }
        if (value === undefined) {
            return window.getComputedStyle(this.elements[0] || document.body).getPropertyValue(property);
        }
        this.elements.forEach(el => (el.style as any)[property] = value);
        return this;
    }

    show(): this {
        this.elements.forEach(el => {
            el.style.display = '';
            el.style.visibility = 'visible';
        });
        return this;
    }

    hide(): this {
        this.elements.forEach(el => {
            el.style.display = 'none';
        });
        return this;
    }

    toggle(show?: boolean): this {
        this.elements.forEach(el => {
            if (show === undefined) {
                // Toggle based on current state
                const isHidden = el.style.display === 'none' || 
                                window.getComputedStyle(el).display === 'none';
                el.style.display = isHidden ? '' : 'none';
            } else {
                el.style.display = show ? '' : 'none';
            }
        });
        return this;
    }

    on(event: string, handlerOrSelector: EventListener | string, handlerOrOptions?: EventListener | boolean | AddEventListenerOptions, options?: boolean | AddEventListenerOptions): this {
        // Support jQuery-style event delegation: on(event, selector, handler)
        if (typeof handlerOrSelector === 'string') {
            // Event delegation: on('click', 'button.destroy-save', handler)
            const selector = handlerOrSelector;
            const handler = handlerOrOptions as EventListener;
            const opts = options;
            this.elements.forEach(el => {
                el.addEventListener(event, (e: Event) => {
                    // Find the closest matching element (handles event bubbling)
                    let target = e.target as HTMLElement | null;
                    while (target && target !== el) {
                        if (target.matches && target.matches(selector)) {
                            handler.call(target, e);
                            break;
                        }
                        target = target.parentElement;
                    }
                }, opts);
            });
        } else {
            // Normal event binding: on('click', handler)
            const handler = handlerOrSelector as EventListener;
            const opts = handlerOrOptions as boolean | AddEventListenerOptions | undefined;
            this.elements.forEach(el => el.addEventListener(event, handler, opts));
        }
        return this;
    }

    off(event: string, handler: EventListener, options?: boolean | EventListenerOptions): this {
        this.elements.forEach(el => el.removeEventListener(event, handler, options));
        return this;
    }

    click(handler?: EventListener): this {
        if (handler) {
            return this.on('click', handler);
        }
        this.elements.forEach(el => el.click());
        return this;
    }

    hover(enterHandler: EventListener, leaveHandler?: EventListener): this {
        this.elements.forEach(el => {
            el.addEventListener('mouseenter', enterHandler);
            if (leaveHandler) {
                el.addEventListener('mouseleave', leaveHandler);
            }
        });
        return this;
    }

    focus(): this {
        (this.elements[0] as HTMLElement & { focus?: () => void })?.focus?.();
        return this;
    }

    val(value?: string): this | string {
        const el = this.elements[0] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        if (value === undefined) {
            return el?.value || '';
        }
        if (el) el.value = value;
        return this;
    }

    data(key: string, value?: any): this | any {
        if (value === undefined) {
            // Reading: check both dataset (camelCase) and attribute (hyphenated)
            const el = this.elements[0];
            if (!el) return undefined;
            
            // Convert hyphenated key to camelCase for dataset API
            const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
            
            // Try dataset first (preferred)
            if (el.dataset && camelKey in el.dataset) {
                return el.dataset[camelKey];
            }
            
            // Fallback to attribute
            return el.getAttribute(`data-${key}`);
        }
        
        // Writing: set both dataset (camelCase) and attribute (hyphenated) for compatibility
        this.elements.forEach(el => {
            // Convert hyphenated key to camelCase for dataset API
            const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
            const stringValue = String(value);
            
            // Set via dataset API (camelCase)
            if (el.dataset) {
                try {
                    (el.dataset as any)[camelKey] = stringValue;
                } catch (e) {
                    // If dataset fails, use setAttribute
                    el.setAttribute(`data-${key}`, stringValue);
                }
            } else {
                el.setAttribute(`data-${key}`, stringValue);
            }
            
            // Also set attribute for compatibility (hyphenated)
            el.setAttribute(`data-${key}`, stringValue);
        });
        return this;
    }

    animate(properties: Record<string, string | number>, duration?: number): this {
        // Simple animation using CSS transitions
        this.elements.forEach(el => {
            const originalTransition = el.style.transition;
            if (duration) {
                el.style.transition = `all ${duration}ms`;
            }
            Object.entries(properties).forEach(([key, value]) => {
                (el.style as any)[key] = typeof value === 'number' ? `${value}px` : value;
            });
            if (duration) {
                setTimeout(() => {
                    el.style.transition = originalTransition;
                }, duration);
            }
        });
        return this;
    }

    find(selector: string): DOMHelper {
        const found: HTMLElement[] = [];
        this.elements.forEach(el => {
            found.push(...Array.from(el.querySelectorAll<HTMLElement>(selector)));
        });
        return new DOMHelper(found);
    }

    parent(): DOMHelper {
        const parents = this.elements.map(el => el.parentElement).filter(Boolean) as HTMLElement[];
        return new DOMHelper(parents);
    }

    children(selector?: string): DOMHelper {
        const children: HTMLElement[] = [];
        this.elements.forEach(el => {
            if (selector) {
                children.push(...Array.from(el.querySelectorAll<HTMLElement>(selector)));
            } else {
                children.push(...Array.from(el.children) as HTMLElement[]);
            }
        });
        return new DOMHelper(children);
    }

    width(): number {
        return this.elements[0]?.offsetWidth || 0;
    }

    height(): number {
        return this.elements[0]?.offsetHeight || 0;
    }

    scrollTop(value?: number): this | number {
        if (value === undefined) {
            return this.elements[0]?.scrollTop || 0;
        }
        this.elements.forEach(el => el.scrollTop = value);
        return this;
    }

    scrollHeight(): number {
        return this.elements[0]?.scrollHeight || 0;
    }
}

/**
 * Create a DOMHelper instance (jQuery-like API)
 */
export function $dom(selector: string | HTMLElement | HTMLElement[]): DOMHelper {
    return new DOMHelper(selector);
}

/**
 * Ready function (replaces $(document).ready())
 */
export function ready(fn: () => void): void {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', fn);
    } else {
        fn();
    }
}

/**
 * Window width/height helpers
 */
export function windowWidth(): number {
    return window.innerWidth;
}

export function windowHeight(): number {
    return window.innerHeight;
}

/**
 * Standalone css function for setting styles
 */
export function css(element: DOMHelper | HTMLElement, property: string, value: string): void {
    if (element instanceof DOMHelper) {
        element.css(property, value);
    } else if (element) {
        (element.style as any)[property] = value;
    }
}
